import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  Box,
  Button,
  Grid,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  AppBar,
  Toolbar,
  Switch,
  ListItemButton,
  FormControlLabel,
  CircularProgress,
  Badge,
} from "@mui/material";
import {
  Chat as ChatIcon,
  Refresh as RefreshIcon,
  Send as SendIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";

import { api } from "../../../hooks/apiClient";
import { useSocket } from "../../../hooks/useSocket";
import { useSnackbar } from "notistack";
import { format, isToday, isYesterday } from "date-fns";
const Messages = ({
  setChats,
  selectedChat,
  setSelectedChat,
  storePhoneId,
  msgLoading,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const messagesEndRef = useRef(null);
  const { socket, isConnected } = useSocket();
  const [newMessage, setNewMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (isToday(date)) {
      return format(date, "HH:mm");
    } else if (isYesterday(date)) {
      return "Yesterday";
    } else {
      return format(date, "dd/MM/yyyy");
    }
  };
  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedChat || !storePhoneId) return;
    setSendingMessage(true);
    try {
      const response = await api.post(`whatsapp/send-message`, {
        phoneNumberId: storePhoneId,
        to: selectedChat.customerNumber,
        newMessage: newMessage.trim(),
      });
      //console.log(response);
      if (response.data) {
        // Update the selected chat with the new message
        const updatedChat = {
          ...selectedChat,
          Messages: [
            ...selectedChat.Messages,
            {
              ConversationId: selectedChat.id,
              content: newMessage.trim(),
              direction: "outbound",
              status: "sent",
              status_status: "sent",
              createdAt: new Date().toISOString(), // ✅ Use this for the key
              updatedAt: new Date().toISOString(),

              contentType: "text",
            },
          ],
        };

        setSelectedChat(updatedChat);

        setChats((prevChats) =>
          prevChats.map((chat) =>
            chat.id === selectedChat.id ? updatedChat : chat,
          ),
        );
      }

      setNewMessage("");
      enqueueSnackbar("Message sent", { variant: "success" });

      // Auto-scroll to bottom after sending message
      // setTimeout(() => {
      //   messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      // }, 100);
    } catch (error) {
      console.error("Error sending message:", error);
      const errorMessage =
        error.response?.data?.error || "Failed to send message";
      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setSendingMessage(false);
    }
  };
  useEffect(() => {
    if (msgLoading) return;
    if (messagesEndRef.current && selectedChat?.Messages) {
      const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      };
      requestAnimationFrame(() => {
        scrollToBottom();
      });
    }
  }, [selectedChat?.Messages, msgLoading]);
  useEffect(() => {
    if (!socket) return;

    const handleNewMessage = (data) => {
      const from = data.from;

      setChats((prevChats) => {
        let updatedChat = null;

        const updated = prevChats.map((chat) => {
          if (chat.customerNumber === from) {
            const newMessage = {
              ConversationId: chat.id,
              content: data.message?.trim() || "",
              direction: "inbound",
              status: "sent",
              status_status: "sent",
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              contentType: "text",
            };

            const isOpen = selectedChat?.id === chat.id;

            updatedChat = {
              ...chat,
              unreadCount: isOpen
                ? chat.unreadCount
                : (chat.unreadCount || 0) + 1,
              Messages: [...(chat.Messages || []), newMessage],
              updatedAt: new Date().toISOString(),
            };

            // update open chat instantly
            if (isOpen) {
              setSelectedChat(updatedChat);
            }

            return updatedChat;
          }

          return chat;
        });

        // 🔥 move updated chat to top
        return updatedChat
          ? [updatedChat, ...updated.filter((c) => c.id !== updatedChat.id)]
          : prevChats;
      });
    };

    socket.on("incoming_message", handleNewMessage);

    return () => {
      socket.off("incoming_message", handleNewMessage);
    };
  }, [socket, selectedChat, setChats, setSelectedChat]);

  // useEffect(() => {
  //   if (!socket) return;

  //   const handleNewMessage = (data) => {
  //     setChats((prevChats) =>
  //       prevChats.map((chat) => {
  //         if (chat.customerNumber === data.from) {
  //           const newMessage = {
  //             ConversationId: chat.id,
  //             content: data.message.trim(),
  //             direction: "inbound",
  //             status: "sent",
  //             status_status: "sent",
  //             createdAt: new Date().toISOString(),
  //             updatedAt: new Date().toISOString(),
  //             contentType: "text",
  //           };

  //           const updatedChat = {
  //             ...chat,
  //             unreadCount:
  //               selectedChat && selectedChat.id === chat.id
  //                 ? chat.unreadCount // don’t bump if it’s the open chat
  //                 : chat.unreadCount + 1,
  //             Messages: [...(chat.Messages || []), newMessage],
  //           };

  //           // if it’s the currently open chat, update selectedChat too
  //           if (selectedChat && selectedChat.id === chat.id) {
  //             setSelectedChat(updatedChat);
  //           }
  //           return updatedChat;
  //         }
  //         return chat;
  //       }),
  //     );
  //   };

  //   socket.on("incoming_message", handleNewMessage);

  //   return () => {
  //     socket.off("incoming_message", handleNewMessage);
  //   };
  // }, [socket, selectedChat]);

  return (
    <>
      {selectedChat ? (
        <>
          <Paper
            sx={{
              p: 2,
              borderBottom: 1,
              borderColor: "divider",
              borderRadius: 0,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <IconButton
                onClick={() => setSelectedChat(null)}
                sx={{ display: { md: "none" }, mr: 1 }}
              >
                <ArrowBackIcon />
              </IconButton>
              <Avatar sx={{ mr: 2 }}>
                {selectedChat.customerNumber
                  ? selectedChat.customerNumber.charAt(5)
                  : "U"}
              </Avatar>
              <Box>
                <Typography variant="h6">
                  {selectedChat.customerNumber}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Last seen {formatDate(selectedChat.updatedAt)}
                </Typography>
              </Box>
            </Box>
          </Paper>

          <Box sx={{ flexGrow: 1, overflow: "auto", p: 2, bgcolor: "grey.50" }}>
            {selectedChat.Messages.map((msg, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  justifyContent:
                    msg.direction === "outbound" ? "flex-end" : "flex-start",
                  mb: 2,
                }}
              >
                <Paper
                  elevation={1}
                  sx={{
                    p: 1.5,
                    maxWidth: "70%",
                    backgroundColor:
                      msg.direction === "outbound"
                        ? "primary.light"
                        : "background.paper",
                    color:
                      msg.direction === "outbound"
                        ? "primary.contrastText"
                        : "text.primary",
                  }}
                >
                  <Typography variant="body1">{msg.content}</Typography>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      mt: 0.5,
                    }}
                  >
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      {msg.direction === "outbound"
                        ? msg.status_status + ".."
                        : ""}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      {formatDate(msg.createdAt)}
                    </Typography>
                  </Box>
                </Paper>
              </Box>
            ))}

            {/* Sentinel for auto-scrolling to bottom */}
            <div ref={messagesEndRef} />
          </Box>

          <Box sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Type a message..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              disabled={sendingMessage}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={sendMessage}
                      disabled={!newMessage.trim() || sendingMessage}
                      color="primary"
                    >
                      <SendIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            color: "text.secondary",
            p: 3,
          }}
        >
          <ChatIcon sx={{ fontSize: 60, mb: 2, opacity: 0.5 }} />
          <Typography variant="h6" gutterBottom>
            Select a conversation
          </Typography>
          <Typography variant="body2" align="center">
            Choose a chat from the list to start messaging or create a new
            conversation
          </Typography>
        </Box>
      )}
    </>
  );
};

export default Messages;
